import useModules, { isModuleEnabled } from "../../hooks/useModules";

/*
 * Master switch for the whole CMMS module.
 *
 * CMMS is unreleased while the OEE side runs in 100+ factories, and merging this branch
 * would otherwise put a CMMS menu, a report button on machine details and an always-on
 * SignalR connection in front of every user. This is the off switch.
 *
 * It reads /api/modules, not /api/features. CMMS is a product part a customer has bought —
 * the same kind of thing as improvements and activities — not a behaviour switch inside OEE,
 * which is what FeatureManagement is for. It sat in the flag list only because FE-0.1 needed
 * an off switch quickly and isFeatureEnabled was what existed.
 *
 * It still fails safe by construction. Modules:Cmms:Enabled ships as the literal "false" in
 * appsettings.json, so /api/modules does not list "cmms" and isModuleEnabled returns false
 * until an environment opts in — doing nothing keeps CMMS invisible. isModuleEnabled is false
 * while the query is loading and false if it fails, so neither state can flash the module
 * into view.
 *
 * The key is declared, rather than left out, because the release pipeline's FileTransform step
 * substitutes only JSON keys that already exist and creates none: a key absent from the shared
 * template is one no variable group can ever set. Its value is a literal and not a
 * "$(CmmsEnabled)" token — the task matches on the variable name, not on the text, so a token
 * would only be decoration that fails to parse as a bool wherever it survives the deploy,
 * throwing out of ModuleService.Get() and 500ing /api/modules for that whole installation.
 *
 * To switch it on: define Modules.Cmms.Enabled in the environment's release variable group, or
 * locally in appsettings.<Environment>.json:
 *     "Modules": { "Cmms": { "Enabled": true } }
 */
export const CMMS_MODULE = "cmms";

export function useIsCmmsEnabled() {
  const modulesQuery = useModules();
  return isModuleEnabled(modulesQuery, CMMS_MODULE);
}

export default useIsCmmsEnabled;
